import { confirmApp, showAppAlert } from '../common/appDialog';

import React, { useState, useEffect } from 'react';
import { ICONS } from './constants';
import { 
    autoGenerateSrmProject,
    generateEpThreatAnalysis,
    generateDailyMicroReport,
    generateSrmArchitectSummary,
    generateFacilityProtectionPlan,
    generateSrmDocument,
    generateIntegratedIntelligence
} from '@common/services/geminiService';
import type { SrmArchitectRequest, SrmProject, Organization, GlobalAsset } from '@types';
import { generateSrmModuleReport, isSrmAutomatedWorkflow, isSrmPlanWorkflow, srmModuleInput, srmModuleReportGaps } from '../../functions/src/common/srmModuleReports';
import Loader from './Loader';
import ApiErrorDisplay from './ApiErrorDisplay';
import AddressSelector from './AddressSelector';
import AssetManagerModal from './AssetManagerModal';
import { useGlobalAssets } from '../context/GlobalAssetContext';
import { dashboardCountry } from '../utils/clientDashboardCountries';
import { assetLocationValues } from '../utils/assetLocationFields';
import { stageSrmSetupRisks } from '../utils/srmRisks';
import { cleanSrmSummary } from '../common/srmSummary';
import { db, auth } from '../../firebaseConfig'; // Keep only db/auth here
import { doc, setDoc, updateDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
interface SrmArchitectInputProps {
    onGenerate: (project: SrmProject) => void; // Navigates to Dashboard
    onSave: (project: SrmProject) => Promise<void>; // Saves to Firestore
    onViewProjects: () => void; // Navigates to List
    onNavigate: (nav: { page: any; params?: any }) => void;
    organization?: Organization | null;
    initialAsset?: GlobalAsset;
    onCancel?: () => void;
    onProcessingChange?: (processing: boolean) => void;
}

const initialFormData = (asset?: GlobalAsset): SrmArchitectRequest => {
    const location = asset ? assetLocationValues(asset) : null;
    return {
        orgType: '',
        operationalFootprint: asset?.location?.address || asset?.name || '',
        keyAssets: asset?.name || '',
        threatContext: '',
        country: location?.country || '',
        state: location?.state || '',
        city: location?.city || '',
        address: location?.address || '',
        zip: location?.zip || '',
        lat: typeof location?.lat === 'number' ? location.lat : 0,
        lng: typeof location?.lng === 'number' ? location.lng : 0,
        personnelRegistry: [],
        assetInventory: []
    };
};

const SrmArchitectInput: React.FC<SrmArchitectInputProps> = ({ onGenerate, onSave, onViewProjects, onNavigate, organization, initialAsset, onCancel, onProcessingChange }) => {
    const [formData, setFormData] = useState<SrmArchitectRequest>(() => initialFormData(initialAsset));
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisProgress, setAnalysisProgress] = useState(0);
    const [analysisAction, setAnalysisAction] = useState("");
    const [error, setError] = useState<string | null>(null);
    
    // Global Asset Integration State
    const [showAssetManager, setShowAssetManager] = useState(false);
    const [showImportList, setShowImportList] = useState(false);
    const { assets } = useGlobalAssets();
    const [includePrimaryInInventory, setIncludePrimaryInInventory] = useState(true);
    const primaryAsset = initialAsset || assets.find(a => a.isPrimary);
    
    // Post-generation state
    const [generatedProject, setGeneratedProject] = useState<SrmProject | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const reportTools = { generateIntegratedIntelligence, generateDailyMicroReport, generateEpThreatAnalysis,
        generateFacilityProtectionPlan, generateSrmDocument };
    
    // Auto-load Primary Asset
    useEffect(() => {
        if (primaryAsset && !formData.address && primaryAsset.location) {
            const location = assetLocationValues(primaryAsset);
            setFormData(prev => ({
                ...prev,
                country: prev.country || location.country,
                state: prev.state || location.state,
                city: prev.city || location.city,
                address: location.address,
                zip: prev.zip || location.zip,
                lat: typeof location.lat === 'number' ? location.lat : 0,
                lng: typeof location.lng === 'number' ? location.lng : 0,
            }));
        }
    }, [primaryAsset, assets]); // Trigger when assets load or primary changes

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.orgType || !formData.operationalFootprint || !formData.country || !organization?.id) {
            const missing = [];
            if (!formData.orgType) missing.push("Organization Type");
            if (!formData.operationalFootprint) missing.push("Operational Footprint");
            if (!formData.country) missing.push("Country");
            if (!organization?.id) missing.push("valid Organization Profile");
            
            setError(`Missing required mission parameters: ${missing.join(", ")}`);
            return;
        }

        setIsAnalyzing(true);
        onProcessingChange?.(true);
        setAnalysisProgress(5);
        setAnalysisAction("Initializing Intelligence Protocol...");
        setError(null);

        // Prepare final form data with primary asset if toggled
        let finalFormData = { ...formData };
        if (includePrimaryInInventory && primaryAsset) {
            const inventory = formData.assetInventory || [];
            const alreadyInInventory = inventory.some(a => a.name === primaryAsset.name);
            if (!alreadyInInventory) {
                finalFormData.assetInventory = [
                    ...inventory,
                    {
                        name: primaryAsset.name,
                        location: primaryAsset.location?.address || 'Primary Location',
                        criticality: primaryAsset.criticality,
                        type: primaryAsset.type
                    }
                ];
            }
        }

        // Give each framework its own record, including separate facilities for one user.
        const tempProjectId = `PROJ-${organization.id}-${auth.currentUser?.uid}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        
        try {
            setIsAnalyzing(true);
            setError(null);
            
            // 0. Cleanup orphaned/stale drafts for this organization
            if (db && organization && auth.currentUser) {
                const staleQuery = query(
                    collection(db, 'srm_projects'),
                    where('organizationId', '==', organization.id),
                    where('userId', '==', auth.currentUser.uid),
                    where('status', '==', 'Compiling')
                );
                const staleSnap = await getDocs(staleQuery);
                const deletePromises = staleSnap.docs.map((staleDoc: any) => deleteDoc(staleDoc.ref));
                await Promise.all(deletePromises);
            }

            // 1. Initialize Global Blueprint Record
            if (db) {
                const draftProject: any = {
                    id: tempProjectId,
                    name: `${formData.orgType} SRM Blueprint`,
                    projectName: `${formData.orgType} SRM Blueprint`,
                    organizationId: organization.id,
                    ...(auth.currentUser?.uid ? { userId: auth.currentUser.uid } : {}),
                    ...(initialAsset ? { assetId: initialAsset.id, locationName: initialAsset.name } : {}),
                    ...(finalFormData.address ? { locationAddress: finalFormData.address } : {}),
                    ...(finalFormData.country ? { country: finalFormData.country } : {}),
                    createdAt: new Date().toISOString(),
                    status: 'Compiling',
                    progress: 5,
                    currentAction: 'Analyzing mission parameters...',
                    summary: 'Strategic Risk Architecture in progress...',
                    modules: [],
                    personnelRegistry: formData.personnelRegistry || [],
                    assetInventory: formData.assetInventory || []
                };
                await setDoc(doc(db, 'srm_projects', tempProjectId), draftProject);
            }

            const sanitizeForFirestore = (obj: any): any => {
                if (Array.isArray(obj)) return obj.map(sanitizeForFirestore);
                if (obj !== null && typeof obj === 'object') {
                    return Object.fromEntries(
                        Object.entries(obj)
                            .filter(([, v]) => v !== undefined)
                            .map(([k, v]) => [k, sanitizeForFirestore(v)])
                    );
                }
                return obj;
            };

            const updateProgress = async (prog: number, action: string, partialProject?: Partial<SrmProject>) => {
                setAnalysisProgress(prog);
                setAnalysisAction(action);
                if (db) {
                    const payload: Record<string, any> = { progress: prog, currentAction: action };
                    if (partialProject) Object.assign(payload, sanitizeForFirestore(partialProject));
                    await updateDoc(doc(db, 'srm_projects', tempProjectId), payload);
                }
            };

            // 2. STAGE 1: Architecting Global Framework
            await updateProgress(15, "Architecting Global Framework structure...");
            const blueprint = await autoGenerateSrmProject(finalFormData);
            
            // 3. STAGE 2: Deep Analysis (Tool Suite Execution)
            const modulesWithResults = [...(blueprint.modules || [])].sort((a, b) =>
                Number(isSrmPlanWorkflow(a.workflowId)) - Number(isSrmPlanWorkflow(b.workflowId)));
            const totalModules = modulesWithResults.length;
            
            for (let i = 0; i < totalModules; i++) {
                const module = modulesWithResults[i];
                delete module.resultData;
                delete module.reportGeneration;
                module.status = 'Draft';
                const moduleProgressStart = 20 + (i * (70 / totalModules));
                const moduleProgressEnd = 20 + ((i + 1) * (70 / totalModules));
                
                // Only run AI analysis for automated workflows
                const isAutomated = isSrmAutomatedWorkflow(module.workflowId);
                module.tag = isAutomated ? 'AI-Automated' : 'User-Required';
                module.prefillData = srmModuleInput({ ...finalFormData, organizationId: organization.id }, module);
                
                if (isAutomated) {
                    await updateProgress(moduleProgressStart, `Automating ${module.title}...`, { modules: modulesWithResults || [] });

                    try {
                        module.resultData = await generateSrmModuleReport({ ...finalFormData, organizationId: organization.id, srmProjectId: tempProjectId,
                            modules: modulesWithResults }, module, reportTools);
                        module.reportGeneration = { status: 'ready', completedAt: Date.now() };
                        module.status = srmModuleReportGaps(module).length ? 'Review Needed' : 'Ready';
                    } catch (toolErr) {
                        console.error(`Automated tool failed for ${module.title}:`, toolErr);
                        module.status = 'Draft';
                        module.reportGeneration = { status: 'error', completedAt: Date.now(),
                            error: toolErr instanceof Error ? toolErr.message : 'Report generation failed. Retry this analysis.' };
                    }
                } else {
                    // User Pathway: Skip AI generation, keep as Draft
                    await updateProgress(moduleProgressStart, `Queueing ${module.title} for user completion...`, { modules: modulesWithResults || [] });
                    module.status = 'Draft';
                    module.reportGeneration = { status: 'input_required' };
                }
                
                await updateProgress(moduleProgressEnd, `Processed ${module.title}`, { modules: modulesWithResults || [] });
            }

            // 4. Final Synth: Generate the Executive Analysis Summary
            await updateProgress(95, "Synthesizing Mission Blueprint...");
            try {
                const finalSummary = await generateSrmArchitectSummary({ ...blueprint, modules: modulesWithResults }, finalFormData);
                blueprint.summary = finalSummary; // Ensure 'summary' is set
            } catch (synthErr) {
                console.error("Summary synthesis failed:", synthErr);
                const fallback = "Framework saved for review. Check each report for gaps and complete the site audit inputs.";
                blueprint.summary = fallback;
            }

            // 5. Finalize and Save Project
            const date = new Date();
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
            const projectNumber = `SRM-${year}-${month}-${random}`;
            const resolvedIso3 = dashboardCountry(initialAsset?.iso3)?.iso3 || dashboardCountry(finalFormData.country)?.iso3;

            const finalProject: SrmProject = {
                ...blueprint,
                id: tempProjectId,
                projectNumber, // <--- UNIQUE IDENTIFIER
                name: blueprint.name || `${formData.orgType} SRM Blueprint`,
                projectName: blueprint.name || blueprint.projectName || `${formData.orgType} SRM Blueprint`,
                summary: cleanSrmSummary(blueprint.summary || ''),
                architectRequest: finalFormData,
                organizationId: organization?.id || '',
                ...(auth.currentUser?.uid ? { userId: auth.currentUser.uid } : {}),
                ...(initialAsset ? { assetId: initialAsset.id, locationName: initialAsset.name } : {}),
                ...(finalFormData.address ? { locationAddress: finalFormData.address } : {}),
                ...(finalFormData.country ? { country: finalFormData.country } : {}),
                ...(resolvedIso3 ? { iso3: resolvedIso3 } : {}),
                ...(finalFormData.lat ? { lat: Number(finalFormData.lat) } : {}),
                ...(finalFormData.lng ? { lng: Number(finalFormData.lng) } : {}),
                status: 'Review',
                progress: 100,
                currentAction: modulesWithResults.some(module => srmModuleReportGaps(module).length)
                    ? 'Framework saved with report gaps. Review and retry incomplete analyses.' : 'Framework Ready for Review.',
                modules: modulesWithResults,
                risks: stageSrmSetupRisks(modulesWithResults, blueprint.risks)
            };
            delete finalProject.overallRiskScore;

            if (db) {
                await setDoc(doc(db, 'srm_projects', tempProjectId), sanitizeForFirestore(finalProject));
            }

            setGeneratedProject(finalProject);
        } catch (err: any) {
            console.error("Architect Error:", err);
            setError(err.message || "Intelligence Protocol interrupted. Partial results saved.");
            if (db) {
                await updateDoc(doc(db, 'srm_projects', tempProjectId), { 
                    status: 'Error',
                    currentAction: 'Analysis failed.'
                });
            }
        } finally {
            setIsAnalyzing(false);
            onProcessingChange?.(false);
        }
    };

    const handleDiscard = async () => {
        if (!generatedProject) return;
        
        const confirmDiscard = await confirmApp("Are you sure you want to discard this proposed framework? This will delete the draft record and clear all parameters.");
        if (!confirmDiscard) return;

        setIsSaving(true);
        try {
            if (db && generatedProject.id) {
                await deleteDoc(doc(db, 'srm_projects', generatedProject.id));
            }
            // Reset everything
            setGeneratedProject(null);
            setFormData(initialFormData(initialAsset));
            setError(null);
        } catch (e) {
            console.error("Error discarding project:", e);
            void showAppAlert("Failed to discard project properly from database.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleClearForm = async () => {
        if (await confirmApp("Clear all mission parameters and start fresh?")) {
            setFormData(initialFormData(initialAsset));
            setError(null);
        }
    };

    const handleChange = (e: any) => {
        const name = e.target.name;
        const value = e.target.value;
        setFormData((prev: any) => ({ ...prev, [name]: value }));
    };

    const addPersonnel = () => {
        const newPerson: any = { name: '', role: '', priority: 'Medium' };
        setFormData((prev: any) => ({
            ...prev,
            personnelRegistry: [...(prev.personnelRegistry || []), newPerson]
        }));
    };

    const removePersonnel = (index: number) => {
        setFormData((prev: any) => ({
            ...prev,
            personnelRegistry: prev.personnelRegistry?.filter((_: any, i: number) => i !== index)
        }));
    };

    const updatePersonnel = (index: number, field: string, value: string) => {
        setFormData((prev: any) => ({
            ...prev,
            personnelRegistry: prev.personnelRegistry?.map((p: any, i: number) => i === index ? { ...p, [field]: value } : p)
        }));
    };

    const addAsset = () => {
        const newAsset: any = { name: '', type: '', location: '', criticality: 'Medium' };
        setFormData((prev: any) => ({
            ...prev,
            assetInventory: [...(prev.assetInventory || []), newAsset]
        }));
    };

    const removeAsset = (index: number) => {
        setFormData((prev: any) => ({
            ...prev,
            assetInventory: prev.assetInventory?.filter((_: any, i: number) => i !== index)
        }));
    };

    const updateAsset = (index: number, field: string, value: string) => {
        setFormData((prev: any) => ({
            ...prev,
            assetInventory: prev.assetInventory?.map((a: any, i: number) => i === index ? { ...a, [field]: value } : a)
        }));
    };

    const handleImportAsset = (asset: GlobalAsset) => {
        const newAsset: any = { 
            name: asset.name, 
            type: asset.type, 
            location: asset.location?.address || `${asset.location?.lat}, ${asset.location?.lng}`, 
            criticality: asset.criticality 
        };
        setFormData((prev: any) => ({
            ...prev,
            assetInventory: [...(prev.assetInventory || []), newAsset]
        }));
        setShowImportList(false);
    };

    const handleSaveOnly = async () => {
        if (!generatedProject) return;
        setIsSaving(true);
        try {
            await onSave(generatedProject);
            void showAppAlert("Project saved to library successfully.");
            // Optionally reset form or redirect
        } catch (e) {
            console.error(e);
            void showAppAlert("Failed to save project.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleMoveToDashboard = async () => {
        if (!generatedProject) return;
        setIsSaving(true);
        try {
            // Save first to ensure persistence
            await onSave(generatedProject);
            onGenerate(generatedProject); // This navigates to the dashboard
        } catch (e) {
            console.error(e);
            void showAppAlert("Failed to save and open project.");
            setIsSaving(false); // Only stop loading on error, otherwise navigation happens
        }
    };

    const retryModule = async (moduleId: string) => {
        if (!generatedProject || isSaving)
            return;
        const module = generatedProject.modules.find(candidate => candidate.id === moduleId);
        if (!module || !isSrmAutomatedWorkflow(module.workflowId))
            return;
        setIsSaving(true);
        const updated = { ...module };
        try {
            updated.resultData = await generateSrmModuleReport(generatedProject, module, reportTools);
            updated.reportGeneration = { status: 'ready', completedAt: Date.now() };
            updated.status = srmModuleReportGaps(updated).length ? 'Review Needed' : 'Ready';
        } catch (error) {
            updated.reportGeneration = { status: 'error', completedAt: Date.now(),
                error: error instanceof Error ? error.message : 'Report generation failed. Retry this analysis.' };
        }
        const modules = generatedProject.modules.map(candidate => candidate.id === moduleId ? updated : candidate);
        const next = { ...generatedProject, modules, risks: stageSrmSetupRisks(modules, generatedProject.risks) };
        try {
            if (db)
                await updateDoc(doc(db, 'srm_projects', generatedProject.id), JSON.parse(JSON.stringify({ modules, risks: next.risks })));
            setGeneratedProject(next);
        } catch {
            setError('Could not save the retried report. Please retry.');
        } finally {
            setIsSaving(false);
        }
    };

    if (isAnalyzing) {
        return (
            <div className="flex flex-col items-center justify-center h-full min-h-[500px] animate-fade-in text-center px-4">
                <div className="relative w-32 h-32 mb-10">
                    <div className="absolute inset-0 border-4 border-blue-500/20 rounded-full"></div>
                    <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 128 128">
                        <circle
                            cx="64" cy="64" r="60"
                            fill="transparent"
                            stroke="currentColor"
                            strokeWidth="8"
                            strokeDasharray="377"
                            strokeDashoffset={377 - (377 * analysisProgress) / 100}
                            className="text-blue-500 transition-all duration-500 ease-out"
                        />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xl font-bold text-slate-800">{analysisProgress}%</span>
                    </div>
                </div>
                <h3 className="text-3xl font-bold text-slate-800 mb-2">Architecting SRM Framework</h3>
                <p className="text-blue-600 font-medium mb-8 h-6">{analysisAction}</p>
                
                <div className="w-full max-w-md space-y-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500 font-medium">Status Check</span>
                        <span className="text-blue-600 font-bold uppercase tracking-tight text-xs">Background Analysis Active</span>
                    </div>
                    
                    <div className="space-y-3">
                        <div className={`flex items-center gap-3 text-sm transition-opacity duration-300 ${analysisProgress >= 20 ? 'text-green-600' : 'text-slate-400'}`}>
                            <div className={`w-2 h-2 rounded-full ${analysisProgress >= 20 ? 'bg-green-500' : 'bg-slate-300'}`}></div>
                            <span>Mission Parameter Analysis</span>
                        </div>
                        <div className={`flex items-center gap-3 text-sm transition-opacity duration-300 ${analysisProgress >= 50 ? 'text-green-600' : 'text-slate-400'}`}>
                            <div className={`w-2 h-2 rounded-full ${analysisProgress >= 50 ? 'bg-green-500' : 'bg-slate-300'}`}></div>
                            <span>Core Asset Vulnerability Drafting</span>
                        </div>
                        <div className={`flex items-center gap-3 text-sm transition-opacity duration-300 ${analysisProgress >= 80 ? 'text-green-600' : 'text-slate-400'}`}>
                            <div className={`w-2 h-2 rounded-full ${analysisProgress >= 80 ? 'bg-green-500' : 'bg-slate-300'}`}></div>
                            <span>Threat Matrix Synchronization</span>
                        </div>
                        <div className={`flex items-center gap-3 text-sm transition-opacity duration-300 ${analysisProgress >= 100 ? 'text-green-600' : 'text-slate-400'}`}>
                            <div className={`w-2 h-2 rounded-full ${analysisProgress >= 100 ? 'bg-green-500' : 'bg-slate-300'}`}></div>
                            <span>Final Framework Integration</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (generatedProject) {
        return (
            <div className="animate-fade-in max-w-4xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
                    <div className="bg-green-50 p-8 border-b border-green-100 text-center">
                        <div className="mx-auto bg-green-100 w-16 h-16 rounded-full flex items-center justify-center text-green-600 mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <h2 className="text-3xl font-bold text-slate-800">{generatedProject.modules.some(module => srmModuleReportGaps(module).length)
                            ? 'Framework Needs Review' : 'Framework Blueprint Ready'}</h2>
                        <p className="text-slate-600 mt-2">Insights has architected a bespoke security solution based on your parameters.</p>
                    </div>
                    
                    <div className="p-8 space-y-6">
                        <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                            <h3 className="text-lg font-bold text-slate-800 mb-2">{generatedProject.name}</h3>
                            <p className="text-slate-600 text-sm leading-relaxed">{cleanSrmSummary(generatedProject.summary)}</p>
                        </div>

                        <div>
                            <h4 className="font-semibold text-slate-700 mb-4">Proposed Framework Components</h4>
                            {error && <p role="alert" className="mb-4 text-sm text-red-700">{error}</p>}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {generatedProject.modules.map((mod, i) => (
                                    <div key={i} className="p-4 border border-slate-200 rounded-lg flex items-start gap-3">
                                        <div className="mt-1 text-blue-600">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-slate-800 text-sm">{mod.title}</p>
                                            <p className="text-xs text-slate-500">{mod.description}</p>
                                            <p className="mt-2 text-xs font-bold text-slate-700">{mod.status}</p>
                                            {srmModuleReportGaps(mod).map(gap => <p key={gap} role="status" className="mt-2 text-xs text-amber-700">{gap}</p>)}
                                            {isSrmAutomatedWorkflow(mod.workflowId) && srmModuleReportGaps(mod).length > 0 &&
                                                <button type="button" disabled={isSaving} onClick={() => void retryModule(mod.id)}
                                                    className="mt-3 rounded-lg bg-blue-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-50">Retry analysis</button>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="p-6 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row justify-end gap-4">
                        <button 
                            onClick={handleDiscard}
                            disabled={isSaving}
                            className="px-6 py-3 text-red-600 font-semibold bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center justify-center"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            Discard & Reset Architect
                        </button>
                        <button 
                            onClick={handleSaveOnly}
                            disabled={isSaving}
                            className="px-6 py-3 text-slate-700 font-semibold bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                        >
                            {isSaving ? 'Saving...' : 'Save for Future Work'}
                        </button>
                        <button 
                            onClick={handleMoveToDashboard}
                            disabled={isSaving}
                            className="px-6 py-3 text-white font-bold bg-blue-600 rounded-lg hover:bg-blue-700 shadow-lg transition-colors disabled:opacity-50 flex items-center justify-center"
                        >
                            {isSaving ? 'Processing...' : 'Move to SRM Project Management Dashboard'}
                            {!isSaving && <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" /></svg>}
                        </button>
                    </div>
                </div>
            </div>
        );
    }


    return (
        <>
            <div className="animate-fade-in max-w-3xl mx-auto">
                <div className="flex justify-between items-center mb-6">
                    <button 
                        onClick={() => onCancel ? onCancel() : onNavigate({ page: 'organizationDashboard' })}
                        className="flex items-center text-sm font-medium text-slate-500 hover:text-blue-600 transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                        {onCancel ? 'Back to Facilities' : 'Back to Hub'}
                    </button>
                    {!onCancel && <button
                        onClick={onViewProjects}
                        className="text-sm font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg transition-colors flex items-center"
                    >
                        {React.cloneElement(ICONS.folder as any, { className: 'h-4 w-4 mr-2' })}
                        View Project Management Dashboard
                    </button>}
                </div>

                <div className="bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-slate-700">
                    <div className="bg-slate-800/50 p-8 border-b border-slate-700">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="p-3 bg-blue-600 rounded-xl text-white shadow-lg shadow-blue-900/50">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6a1.125 1.125 0 0 1-1.125-1.125v-3.75ZM14.25 8.625c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v8.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 0 1-1.125-1.125v-8.25ZM3.75 16.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 0 1-1.125-1.125v-2.25Z" /></svg>
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-white">SRM Auto-Architect <span className="text-[10px] text-slate-600 font-mono ml-2">v2026.03.28.1</span></h1>
                                <p className="text-blue-200">Intelligent Framework Generation System</p>
                            </div>
                        </div>
                        <p className="text-slate-400 text-sm">
                            Provide your organization's profile below. Insights will analyze your operational footprint and automatically construct a bespoke Security Risk Management framework, pre-populating relevant tools and workflows.
                        </p>
                        <div className="mt-4 flex justify-end">
                            <button 
                                type="button"
                                onClick={handleClearForm}
                                className="text-xs font-semibold text-slate-500 hover:text-white transition-colors flex items-center gap-1.5"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                Clear All Parameters
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="p-8 space-y-6">
                        {/* Form content ... (omitted for brevity in replacement, but I will include it all in the tool call) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-xs font-bold text-blue-400 uppercase tracking-wider mb-2">Organization Type / Industry</label>
                                <input 
                                    type="text" 
                                    name="orgType"
                                    value={formData.orgType}
                                    onChange={handleChange}
                                    placeholder="e.g., International NGO, Tech Startup, Mining Corp"
                                    className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-blue-400 uppercase tracking-wider mb-2">Operational Footprint</label>
                                <input 
                                    type="text" 
                                    name="operationalFootprint"
                                    value={formData.operationalFootprint}
                                    onChange={handleChange}
                                    placeholder="e.g., HQ in London, field ops in South Sudan & Yemen"
                                    className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
                                    required
                                />
                            </div>
                        </div>

                        <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-6 space-y-4">
                            <h3 className="text-sm font-bold text-blue-300 uppercase tracking-widest flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Primary Location Profile
                            </h3>
                            <p className="text-xs text-slate-500 pb-2">Providing a specific location enables Insights to automatically fetch local threat data and pre-fill security site assessments.</p>
                            <AddressSelector prefix="" formData={formData} onFormChange={handleChange} theme="dark" />
                            
                            {primaryAsset && (
                                <div className="flex items-center gap-2 pt-2 border-t border-slate-700/50 mt-2">
                                    <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 px-3 py-2 rounded-lg w-full">
                                        <input 
                                            type="checkbox" 
                                            id="includePrimary" 
                                            checked={includePrimaryInInventory} 
                                            onChange={(e) => setIncludePrimaryInInventory(e.target.checked)}
                                            className="rounded border-slate-700 bg-slate-900 text-blue-600 focus:ring-blue-500"
                                        />
                                        <label htmlFor="includePrimary" className="text-[10px] font-medium text-blue-300 cursor-pointer select-none flex-1">
                                            Include "{primaryAsset.name}" in Asset Inventory for Comprehensive Analysis
                                        </label>
                                        <span className="text-[8px] bg-blue-500 text-white px-1.5 py-0.5 rounded font-bold uppercase">Primary</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-blue-400 uppercase tracking-wider mb-2">Key Assets to Protect</label>
                            <textarea 
                                name="keyAssets"
                                value={formData.keyAssets}
                                onChange={handleChange}
                                rows={2}
                                placeholder="e.g., Expat staff, local warehouses, server data, vehicle fleet."
                                className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all resize-none"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-blue-400 uppercase tracking-wider mb-2">Threat Context & Concerns</label>
                            <textarea 
                                name="threatContext"
                                value={formData.threatContext}
                                onChange={handleChange}
                                rows={3}
                                placeholder="e.g., High risk of kidnapping, civil unrest, and poor road infrastructure. Concern about supply chain theft."
                                className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all resize-none"
                            />
                        </div>

                        <div className="pt-4 space-y-6">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                                    Personnel Registry
                                </h3>
                                <button type="button" onClick={addPersonnel} className="text-xs font-bold text-blue-400 bg-blue-400/10 hover:bg-blue-400/20 px-3 py-1.5 rounded-full transition-all border border-blue-400/20">
                                    + Add Personnel
                                </button>
                            </div>
                            
                            {formData.personnelRegistry && formData.personnelRegistry.length > 0 ? (
                                <div className="space-y-3">
                                    {formData.personnelRegistry.map((p: any, i: number) => (
                                        <div key={i} className="grid grid-cols-12 gap-3 p-4 bg-slate-800/50 border border-slate-700 rounded-xl relative group">
                                            <div className="col-span-4">
                                                <input 
                                                    type="text" 
                                                    placeholder="Name" 
                                                    value={p.name}
                                                    onChange={(e) => updatePersonnel(i, 'name', e.target.value)}
                                                    className="w-full bg-slate-900 border border-slate-700 rounded-md p-2 text-xs text-white"
                                                />
                                            </div>
                                            <div className="col-span-4">
                                                <input 
                                                    type="text" 
                                                    placeholder="Role" 
                                                    value={p.role}
                                                    onChange={(e) => updatePersonnel(i, 'role', e.target.value)}
                                                    className="w-full bg-slate-900 border border-slate-700 rounded-md p-2 text-xs text-white"
                                                />
                                            </div>
                                            <div className="col-span-3">
                                                <select 
                                                    value={p.priority}
                                                    onChange={(e) => updatePersonnel(i, 'priority', e.target.value)}
                                                    className="w-full bg-slate-900 border border-slate-700 rounded-md p-2 text-xs text-white"
                                                >
                                                    <option value="Low">Low Priority</option>
                                                    <option value="Medium">Medium Priority</option>
                                                    <option value="High">High Priority</option>
                                                </select>
                                            </div>
                                            <div className="col-span-1 flex items-center justify-center">
                                                <button type="button" onClick={() => removePersonnel(i)} className="text-slate-500 hover:text-red-400 transition-colors">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-slate-500 text-xs text-center py-4 bg-slate-800/30 rounded-xl border border-dashed border-slate-700">No personnel registered yet. Add key staff for automated resource allocation analysis.</p>
                            )}
                        </div>

                        <div className="pt-4 space-y-6">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2-2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                                    Asset Inventory
                                </h3>
                                <div className="flex items-center gap-2">
                                    <button 
                                        type="button" 
                                        onClick={() => setShowAssetManager(true)}
                                        className="text-[10px] font-bold text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 px-3 py-1.5 rounded-lg transition-all border border-slate-700 flex items-center gap-1.5"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                        View Organization Locations
                                    </button>
                                    <button 
                                        type="button" 
                                        onClick={() => setShowImportList(!showImportList)}
                                        className="text-[10px] font-bold text-blue-400 bg-blue-400/10 hover:bg-blue-400/20 px-3 py-1.5 rounded-lg transition-all border border-blue-400/20 flex items-center gap-1.5"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                                        Import from Global Assets
                                    </button>
                                    <button type="button" onClick={addAsset} className="text-[10px] font-bold text-white bg-blue-600 hover:bg-blue-500 px-3 py-1.5 rounded-lg transition-all shadow-lg shadow-blue-900/20">
                                        + Add New
                                    </button>
                                </div>
                            </div>

                            {showImportList && (
                                <div className="bg-slate-900 border border-blue-500/30 rounded-xl p-4 animate-in fade-in slide-in-from-top-2 duration-200">
                                    <div className="flex justify-between items-center mb-3">
                                        <h4 className="text-[10px] font-bold text-blue-300 uppercase tracking-wider">Select Asset to Import</h4>
                                        <button onClick={() => setShowImportList(false)} className="text-slate-500 hover:text-white text-xs">✕</button>
                                    </div>
                                    {assets.length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                                            {assets.map((asset) => (
                                                <button 
                                                    key={asset.id}
                                                    type="button"
                                                    onClick={() => handleImportAsset(asset)}
                                                    className="flex flex-col items-start p-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-blue-500/50 rounded-lg transition-all group text-left"
                                                >
                                                    <div className="flex justify-between items-start w-full">
                                                        <span className="text-xs font-bold text-white group-hover:text-blue-300">{asset.name}</span>
                                                        <span className={`text-[8px] px-1.5 py-0.5 rounded ${
                                                            asset.criticality === 'Critical' ? 'bg-red-900 text-red-200' :
                                                            asset.criticality === 'High' ? 'bg-orange-900 text-orange-200' :
                                                            'bg-blue-900 text-blue-200'
                                                        }`}>{asset.criticality}</span>
                                                    </div>
                                                    <span className="text-[9px] text-slate-400 mt-1 line-clamp-1">{asset.location?.address || 'No address'}</span>
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-[10px] text-slate-500 italic">Add headquarters and additional locations on the management site.</p>
                                    )}
                                </div>
                            )}
                            
                            {formData.assetInventory && formData.assetInventory.length > 0 ? (
                                <div className="space-y-3">
                                    {formData.assetInventory.map((a: any, i: number) => (
                                        <div key={i} className="grid grid-cols-12 gap-3 p-4 bg-slate-800/50 border border-slate-700 rounded-xl relative group">
                                            <div className="col-span-4">
                                                <input 
                                                    type="text" 
                                                    placeholder="Asset Name (e.g. Server Room)" 
                                                    value={a.name}
                                                    onChange={(e) => updateAsset(i, 'name', e.target.value)}
                                                    className="w-full bg-slate-900 border border-slate-700 rounded-md p-2 text-xs text-white"
                                                />
                                            </div>
                                            <div className="col-span-4">
                                                <input 
                                                    type="text" 
                                                    placeholder="Location" 
                                                    value={a.location}
                                                    onChange={(e) => updateAsset(i, 'location', e.target.value)}
                                                    className="w-full bg-slate-900 border border-slate-700 rounded-md p-2 text-xs text-white"
                                                />
                                            </div>
                                            <div className="col-span-3">
                                                <select 
                                                    value={a.criticality}
                                                    onChange={(e) => updateAsset(i, 'criticality', e.target.value)}
                                                    className="w-full bg-slate-900 border border-slate-700 rounded-md p-2 text-xs text-white"
                                                >
                                                    <option value="Low">Low Criticality</option>
                                                    <option value="Medium">Medium Criticality</option>
                                                    <option value="High">High Criticality</option>
                                                    <option value="Critical">Mission Critical</option>
                                                </select>
                                            </div>
                                            <div className="col-span-1 flex items-center justify-center">
                                                <button type="button" onClick={() => removeAsset(i)} className="text-slate-500 hover:text-red-400 transition-colors">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-slate-500 text-xs text-center py-4 bg-slate-800/30 rounded-xl border border-dashed border-slate-700">No assets listed yet. Define key physical assets for automated vulnerability mapping.</p>
                            )}
                        </div>

                        <div className="pt-4 border-t border-slate-800 flex justify-end">
                            <button 
                                type="submit" 
                                className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-8 rounded-lg shadow-lg shadow-blue-900/20 transition-all transform hover:-translate-y-0.5 flex items-center gap-2"
                            >
                                <span>Initialize Architect Protocol</span>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </form>
                    
                    <div className="px-8 pb-8">
                        <ApiErrorDisplay error={error} serviceName="SRM Architect" />
                    </div>
                </div>
            </div>

            {showAssetManager && (
                <AssetManagerModal 
                    onClose={() => setShowAssetManager(false)} 
                />
            )}
        </>
    );

};

export default SrmArchitectInput;
